'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';

const COOKIE_CONSENT_KEY = 'sp_cookie_consent';
const OPEN_COOKIE_PREFS_EVENT = 'open-cookie-preferences';

export function openCookiePreferences() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(OPEN_COOKIE_PREFS_EVENT));
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [currentChoice, setCurrentChoice] = useState<string | null>(null);

  const openBanner = useCallback(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    setCurrentChoice(stored);
    setVisible(true);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) {
      setCurrentChoice(null);
      setVisible(true);
    }

    window.addEventListener(OPEN_COOKIE_PREFS_EVENT, openBanner);
    return () => {
      window.removeEventListener(OPEN_COOKIE_PREFS_EVENT, openBanner);
    };
  }, [openBanner]);

  const disableAnalytics = () => {
    // Disable GA by setting the opt-out flag
    const gaId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (gaId && typeof window !== 'undefined') {
      (window as Record<string, unknown>)[`ga-disable-${gaId}`] = true;
    }
  };

  const handleAcceptAll = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'all');
    setVisible(false);
  };

  const handleEssentialOnly = () => {
    const previous = localStorage.getItem(COOKIE_CONSENT_KEY);
    localStorage.setItem(COOKIE_CONSENT_KEY, 'essential');
    // If downgrading from all → essential, disable analytics immediately
    if (previous === 'all') {
      disableAnalytics();
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50"
      style={{ backgroundColor: '#1B2A4A' }}
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
    >
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        {/* Text */}
        <p className="font-body text-sm text-white flex-1 leading-relaxed">
          We use cookies to keep you logged in and to understand how That Clicked is used. No advertising. No selling your data.
        </p>

        {/* Buttons + link */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 flex-shrink-0">
          <button
            onClick={handleAcceptAll}
            className="font-body text-sm font-semibold px-5 py-2 rounded-lg transition-all duration-150 active:scale-95 whitespace-nowrap"
            style={{
              backgroundColor: currentChoice === 'all' ? '#b8741a' : '#D4891A',
              color: 'white',
              outline: currentChoice === 'all' ? '2px solid white' : 'none',
            }}
          >
            Accept all{currentChoice === 'all' ? ' ✓' : ''}
          </button>
          <button
            onClick={handleEssentialOnly}
            className="font-body text-sm font-medium px-5 py-2 rounded-lg border transition-all duration-150 active:scale-95 whitespace-nowrap"
            style={{
              borderColor: currentChoice === 'essential' ? '#D1D5DB' : '#6B7280',
              color: '#D1D5DB',
              backgroundColor: 'transparent',
              outline: currentChoice === 'essential' ? '2px solid #D1D5DB' : 'none',
            }}
          >
            Essential only{currentChoice === 'essential' ? ' ✓' : ''}
          </button>
          <Link
            href="/privacy"
            className="font-body text-xs transition-colors duration-150 hover:text-white whitespace-nowrap"
            style={{ color: '#9CA3AF' }}
          >
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
